
import { Injectable } from '@angular/core';
import { auditTime, delay, distinctUntilChanged, filter, first, map, switchMap, take, takeUntil, tap, timeout, catchError, shareReplay } from 'rxjs/operators';
import { ServerResponse } from '@app/enums/server-response';
import { Store, select } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { LobbyService } from '../../../features/lobby/services/lobby.service';

import * as LobbyActions from '../lobby/lobby.actions';
import * as TournamentSummariesActions from './tournament-summaries.actions';
import * as TournamentSummariesSelectors from './tournament-summaries.selector';
import * as UserSelectors from '@app/store/features/user/user.selectors';

import { WsService } from '@app/services/ws.service';
import { TournamentSummary, TournamentSummaryResponse } from '@app/models/tournament-summary';
import { TournamentInformationResponse } from '@app/models/tournament-information';
import { combineLatest, forkJoin, of, timer, EMPTY } from 'rxjs';
import { isEqual } from 'lodash';
import { LiveLobbyTournament } from '@app/models/live-lobby-tournament';

@Injectable()
export class TournamentsSummariesEffects {
    constructor(
        private _ws: WsService,
        private readonly _store: Store,
        private readonly lobbyService: LobbyService,
        private readonly _actions: Actions,

    ) { }

    // updateActiveNav$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(LobbyActions.updateActiveNav),
    //             filter(({ activeNav }) => activeNav === LobbyNav.Tournaments),
    //             tap(() => this.lobbyService.getTournamentSummaries()),
    //             map(() => TournamentSummariesActions.updateLoader({ loader: true }))
    //         ));


    updateTournamentSummaries$ = createEffect(() =>
        this._ws.getDataResponse<TournamentSummaryResponse[]>(ServerResponse.TournamentsSummaries)
            .pipe(
                map(tournamentSummariesResponse => {
                    const tournamentSummaries: TournamentSummary[] = tournamentSummariesResponse.map(tournamentSummaryResponse => {
                        return this.lobbyService.updateTournamentSummary(tournamentSummaryResponse)
                    })
                    return TournamentSummariesActions.updateAll({ tournamentSummaries })
                })
            ));


    updateTournamentSummary$ = createEffect(() =>
        this._ws.getDataResponse<TournamentSummaryResponse>(ServerResponse.TournamentSummary)
            .pipe(
                map(tournamentSummaryResponse => {
                    const tournamentSummary = this.lobbyService.updateTournamentSummary(tournamentSummaryResponse)
                    return TournamentSummariesActions.updateOne({ id: tournamentSummary.id, tournamentSummary })
                }))
    );


    liveLobbyTournament$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyTournament>(ServerResponse.LiveLobbyTournament)
            .pipe(
                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions
                switchMap(liveLobbyTournament =>
                    this._store.pipe(
                        select(TournamentSummariesSelectors.selectEntityById(liveLobbyTournament.idTournament)),
                        filter(tourSum => !!tourSum),
                        take(1), // 🔧 FIX: Complete inner subscription after first valid value
                        timeout(5000), // 🔧 FIX: Prevent hanging if entity doesn't exist
                        map(tourSum => ({ liveLobbyTournament, tourSum })), // 🔧 FIX: Combine data
                        catchError(() => EMPTY) // 🔧 FIX: Skip if entity not found or timeout
                    )
                ),
                map(({ liveLobbyTournament, tourSum }) => {
                    // Immutable update - no mutation
                    const data: TournamentSummary = {
                        ...tourSum!,
                        status2: liveLobbyTournament.status2,
                        nbPlayerRegistered: liveLobbyTournament.nbPlayersRegistered,
                        nbPlayersActive: liveLobbyTournament.nbPlayersActive,
                        blind: liveLobbyTournament.blind,
                        startIn: liveLobbyTournament.startIn
                    };

                    return TournamentSummariesActions.updateOne({ id: liveLobbyTournament.idTournament, tournamentSummary: data })
                }),
                catchError((error) => {
                    console.warn('liveLobbyTournament$ error:', error);
                    return EMPTY; // 🔧 FIX: Prevent effect from dying on error
                })
            )
    );

    updateTournamentInformationResponse$ = createEffect(() =>
        this._ws.getDataResponse<TournamentInformationResponse>(ServerResponse.TournamentInformation)
            .pipe(
                delay(200), //wait for tournament info to be processed
                auditTime(120), //collapse bursts: keep only the last event every 120ms
                distinctUntilChanged((a, b) => isEqual(a, b)),

                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions on new events
                switchMap((tournamentInformationResponse) => {
                    // 🔧 FIX: Each combineLatest completes with take(1), preventing subscription accumulation
                    return combineLatest([
                        of(tournamentInformationResponse),
                        this._store.pipe(select(UserSelectors.selectUserProfile), take(1)),
                        this._store.pipe(
                            select(TournamentSummariesSelectors.selectEntityById(tournamentInformationResponse.idTournament)),
                            filter(data => !!data),
                            map(data => data!),
                            take(1), // 🔧 FIX: Complete inner subscription
                            timeout(5000), // 🔧 FIX: Prevent hanging
                            catchError(() => EMPTY) // 🔧 FIX: Handle missing entity
                        )
                    ])
                }),
                filter(([_, __, tourSum]) => !!tourSum), // 🔧 FIX: Skip if no tournament found
                map(([tournamentInformationResponse, userProfile, tourSum]) => {
                    const myPlayer = tournamentInformationResponse.players.find(player => player.idPlayer === userProfile?.id);
                    const imOnTour = !myPlayer?.positionEnded ? myPlayer : undefined;
                    const iWasOnTour = myPlayer?.positionEnded ? myPlayer : undefined;
                    const isRegistered = !!(imOnTour && !imOnTour.positionEnded);

                    // Immutable update - no mutation
                    const tourSumGame = {
                        ...tourSum.game,
                        imOnTour: !!imOnTour,
                        iWasOnTour: !!iWasOnTour,
                        isRegistered: isRegistered
                    };

                    return TournamentSummariesActions.updateOne({
                        id: tournamentInformationResponse.idTournament,
                        tournamentSummary: {
                            nbPlayerRegistered: tournamentInformationResponse.nbPlayerRegistered,
                            nbRebuyUsed: tournamentInformationResponse.nbRebuyUsed,
                            nbAddonUsed: tournamentInformationResponse.nbAddonUsed,
                            totalPrizePool: tournamentInformationResponse.totalPrizePool,
                            imRegistered: isRegistered,
                            game: tourSumGame
                        }
                    })
                }),
                catchError((error) => {
                    console.warn('updateTournamentInformationResponse$ error:', error);
                    return EMPTY; // 🔧 FIX: Prevent effect from dying
                })
            )
    );




} 